import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';

const NotificationDropdown = ({ userType }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const endpoint = userType === 'admin' ? '/admin/notifications' : '/organizer/notifications';
      const response = await api.get(endpoint);
      
      if (response.data.success) {
        setNotifications(response.data.data.notifications);
        setUnreadCount(response.data.data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const endpoint = userType === 'admin' 
        ? `/admin/notifications/${notificationId}/read` 
        : `/organizer/notifications/${notificationId}/read`;
      
      await api.put(endpoint);
      
      // Update local state
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      setLoading(true);
      const endpoint = userType === 'admin' 
        ? '/admin/notifications/read-all' 
        : '/organizer/notifications/read-all';
      
      await api.put(endpoint);
      
      // Update local state
      setNotifications(notifications.map(n => ({ ...n, read_at: new Date().toISOString() })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    } finally {
      setLoading(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_organizer':
        return 'fa-user-plus';
      case 'new_event':
        return 'fa-calendar-plus';
      case 'new_order':
        return 'fa-shopping-cart';
      case 'payout_request':
        return 'fa-money-check-alt';
      default:
        return 'fa-bell';
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <li className="nav-item dropdown no-arrow mx-1" ref={dropdownRef}>
      <button
        className="nav-link dropdown-toggle btn btn-link"
        onClick={(e) => {
          e.preventDefault();
          setShowDropdown(!showDropdown);
        }}
        type="button"
      >
        <i className="fas fa-bell fa-fw"></i>
        {unreadCount > 0 && (
          <span className="badge badge-danger badge-counter">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      <div
        className={`dropdown-list dropdown-menu dropdown-menu-right shadow animated--grow-in ${
          showDropdown ? 'show' : ''
        }`}
        style={{ width: '26rem' }}
      >
        <h6 className="dropdown-header">
          Notifications
          {unreadCount > 0 && (
            <button
              className="btn btn-link btn-sm float-right text-primary"
              onClick={markAllAsRead}
              disabled={loading}
              style={{ textDecoration: 'none' }}
            >
              Mark all read
            </button>
          )}
        </h6>

        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {notifications.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="fas fa-bell-slash fa-2x mb-2"></i>
              <p className="mb-0">No notifications</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <button
                key={notification.id}
                className={`dropdown-item d-flex align-items-center ${
                  !notification.read_at ? 'bg-light' : ''
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  if (!notification.read_at) {
                    markAsRead(notification.id);
                  }
                }}
              >
                <div className="mr-3">
                  <div
                    className={`icon-circle ${
                      !notification.read_at ? 'bg-primary' : 'bg-secondary'
                    }`}
                  >
                    <i
                      className={`fas ${getNotificationIcon(notification.type)} ${
                        !notification.read_at ? 'text-white' : 'text-gray-400'
                      }`}
                    ></i>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div className="small text-gray-500">
                    {formatTime(notification.created_at)}
                  </div>
                  <span className={!notification.read_at ? 'font-weight-bold' : ''}>
                    {notification.data?.title}
                  </span>
                  <p className="mb-0 small text-gray-600">
                    {notification.data?.message}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>

        {notifications.length > 0 && (
          <div className="dropdown-item text-center small text-gray-500" style={{cursor: 'default'}}>
            {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </li>
  );
};

export default NotificationDropdown;
